import { useCallback, useEffect, useMemo, useState } from 'react';
import { T } from '../components/text/TextEdit';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Search, Save, RotateCcw } from 'lucide-react';
import { api } from '../lib/api';
import i18n, { applyTextOverrides, SUPPORTED_LANGUAGES } from '../lib/i18n';
import en from '../locales/en.json';
import fa from '../locales/fa.json';
import es from '../locales/es.json';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { SectionCard, Skeleton } from '../components/pipeline/ui';

// Migration 067 — the interface wording, editable from inside the app.
//
// Ali, 2026-08-15: "i want it in the app to see it, And edit it and save it."
//
// WHY A WHOLE-FILE PAGE AND NOT ONLY PER-PHRASE PENS. Pens are better when you can SEE the phrase
// you want to change. This page covers the rest: every key in the language, searchable, including
// the ones that only appear in a state you cannot easily reach (an error toast, an empty state, a
// confirmation dialog). Between them there is no phrase in the product that cannot be edited.
//
// THE SHIPPED FILE IS NEVER TOUCHED. What is stored is an OVERRIDE per key. Clearing a box deletes
// the override and the shipped wording comes back — which is why "revert" needs no separate
// mechanism and no copy of the original: the original was never overwritten.

const BUNDLES = { en, fa, es };

/** locale JSON (nested) -> flat dotted keys, the shape the overrides table stores. Arrays are
 *  skipped deliberately: a few keys hold lists of progress messages, and editing those as one text
 *  box would silently turn a list into a string. */
function flatten(node, prefix = '', out = {}) {
  for (const [k, v] of Object.entries(node || {})) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object' && !Array.isArray(v)) flatten(v, key, out);
    else if (typeof v === 'string') out[key] = v;
  }
  return out;
}

export default function UiTextPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState(null);
  const [lang, setLang] = useState(i18n.language || 'fa');
  const [overrides, setOverrides] = useState(null);
  const [draft, setDraft] = useState({});
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);

  const shipped = useMemo(() => flatten(BUNDLES[lang] || {}), [lang]);

  const load = useCallback((l) => {
    setOverrides(null);
    api.uiText('get', { lang: l })
      .then((r) => { setOverrides(r.overrides || {}); setDraft({}); })
      .catch((e) => { toast.error(e.message); setOverrides({}); });
  }, []);

  useEffect(() => {
    api.getMe().then((r) => {
      if (!r.is_admin) { setAllowed(false); navigate('/'); return; }
      setAllowed(true);
      load(lang);
    }).catch(() => { setAllowed(false); navigate('/'); });
  }, [navigate, load, lang]);

  // What is on screen right now for each key: the override if there is one, else the shipped text.
  const current = useCallback(
    (key) => (key in draft ? draft[key] : (overrides?.[key] ?? shipped[key] ?? '')),
    [draft, overrides, shipped],
  );

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return Object.keys(shipped)
      .filter((k) => !needle
        || k.toLowerCase().includes(needle)
        || String(shipped[k]).toLowerCase().includes(needle)
        || String(overrides?.[k] ?? '').toLowerCase().includes(needle))
      .sort();
  }, [shipped, overrides, q]);

  const dirty = Object.keys(draft).length;

  const save = async () => {
    if (!dirty) return;
    setBusy(true);
    try {
      // Sent as ONE call: a partial save would leave the interface half-edited.
      await api.uiText('set_bulk', { lang, entries: draft });
      // Apply immediately rather than waiting for a reload, so the change is visible on the screen
      // that made it — a save you cannot see reads as a save that did not work.
      const cleared = Object.fromEntries(
        Object.entries(draft).map(([k, v]) => [k, v === '' ? (shipped[k] ?? '') : v]),
      );
      applyTextOverrides(lang, cleared);
      toast.success(t('uiText.saved', { n: dirty }));
      load(lang);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  if (allowed === null) return <Skeleton className="h-10 w-48" />;
  if (allowed === false) return null;

  return (
    <div className="space-y-6" data-testid="ui-text-page">
      <div>
        <h1 className="text-2xl font-semibold text-foreground"><T k="uiText.title" /></h1>
        <p className="text-sm text-muted-foreground mt-1"><T k="uiText.subtitle" /></p>
      </div>

      <SectionCard title={t('uiText.cardTitle')} testId="ui-text-card">
        <div className="flex flex-wrap items-center gap-3 mt-2">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute start-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="ps-8" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={t('uiText.searchPlaceholder')} data-testid="ui-text-search" />
          </div>
          <select
            className="text-sm border border-border rounded-md px-2 py-2 bg-card text-foreground"
            value={lang} onChange={(e) => setLang(e.target.value)} data-testid="ui-text-lang"
          >
            {SUPPORTED_LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <Button onClick={save} disabled={busy || !dirty} data-testid="ui-text-save">
            <Save className="h-4 w-4 me-2" />
            {dirty ? t('uiText.saveN', { n: dirty }) : t('uiText.save')}
          </Button>
        </div>

        <p className="text-xs text-muted-foreground mt-2"><T k="uiText.hint" /></p>

        {overrides === null ? <Skeleton className="h-40 w-full mt-4" /> : (
          <>
            <p className="text-xs text-muted-foreground mt-3" data-testid="ui-text-count">
              {t('uiText.showing', { shown: rows.length, total: Object.keys(shipped).length })}
            </p>
            <div className="mt-2 divide-y divide-border max-h-[65vh] overflow-y-auto">
              {rows.map((k) => {
                const isOverridden = k in (overrides || {});
                const isDirty = k in draft;
                return (
                  <div key={k} className="py-3" data-testid={`ui-text-row-${k}`}>
                    <div className="flex items-center justify-between gap-2">
                      <code className="text-xs text-muted-foreground font-data break-all">{k}</code>
                      {isOverridden && (
                        <button
                          type="button"
                          className="text-xs text-warning inline-flex items-center gap-1 shrink-0"
                          onClick={() => setDraft({ ...draft, [k]: '' })}
                          data-testid={`ui-text-reset-${k}`}
                        >
                          <RotateCcw className="h-3 w-3" />{t('uiText.reset')}
                        </button>
                      )}
                    </div>
                    <Textarea
                      className={`mt-1 text-sm ${isDirty ? 'border-warning' : ''}`}
                      rows={String(current(k)).length > 90 ? 3 : 1}
                      value={current(k)}
                      onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                      data-testid={`ui-text-input-${k}`}
                    />
                    {/* Show the shipped wording under an edited row, so it is always recoverable by
                        eye even before pressing reset. */}
                    {isOverridden && (
                      <p className="text-xs text-muted-foreground mt-1 truncate" title={shipped[k]}>
                        {t('uiText.original')}: {shipped[k]}
                      </p>
                    )}
                  </div>
                );
              })}
              {rows.length === 0 && (
                <p className="py-4 text-sm text-muted-foreground" data-testid="ui-text-empty">
                  {t('uiText.noMatch')}
                </p>
              )}
            </div>
          </>
        )}
      </SectionCard>
    </div>
  );
}
